import { useEditor } from '@/components/Editor/EditorContext';
import { TagIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
  ContentItem,
  Form,
  LeadsDynamicPage,
  SectionData,
} from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import {
  CreatableWebsiteSectionType,
  createWebsiteSection,
} from '../services/WebsitesService';
import { emptyConverter } from '../utils';

interface Props {
  websiteId: string;
  variantId: string;
  dynamicPageId: string;
  open: boolean;
  section?: Partial<SectionData>;
  onClose: () => void;
  onAdd: (section: SectionData) => void;
}

interface AddSectionApprovalModalProps {
  type: CreatableWebsiteSectionType;
  open: boolean;
  onClose: () => void;
  onAdd: (prompt?: string, params?: Record<string, any>) => Promise<void>;
}

function InnerAddSectionApprovalModal({
  type,
  open,
  onClose,
  onAdd,
}: AddSectionApprovalModalProps) {
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPage } = useEditor();

  const [prompt, setPrompt] = useState<string>();
  const [params, setParams] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [comparisonCategories, setComparisonCategories] =
    useState<ContentItem[]>();
  const [forms, setForms] = useState<QueryDocumentSnapshot<Form>[]>();

  useEffect(() => {
    if (!selectedAccount) return;
    if (type !== 'comparisonList' && type !== 'comparisonTopItems') return;
    if (comparisonCategories) return;

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonCategory'),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        setComparisonCategories(items.docs.map((d) => d.data()));
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, comparisonCategories, type]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (type !== 'form' && type !== 'contactUs') return;
    if (forms) return;

    getDocs(
      query(
        collection(selectedAccount.ref, 'forms'),
        where('status', '==', 'active'),
      ).withConverter(emptyConverter<Form>()),
    ).then((items) => {
      setForms(items.docs);
    });
  }, [selectedAccount, type, forms]);

  const onAddClick = useCallback(() => {
    if (!selectedAccount || !dynamicPage) return;
    if (!open) return;

    const selectedDynamicPageData = dynamicPage;

    if (
      (type === 'comparisonList' || type === 'comparisonTopItems') &&
      !params.categoryId
    ) {
      setError(
        'Please select a category, if you do not have any in the list, go to the Content page',
      );
      return;
    }

    if (type === 'form' || type === 'contactUs') {
      const selectedFormId =
        params.formId ||
        ('formId' in selectedDynamicPageData && selectedDynamicPageData.formId);

      if (!selectedFormId) {
        setError(
          'Please select a form, if you do not have any in the list, go to the Forms page',
        );
        return;
      }
    }

    if (
      type === 'trustpilot' &&
      (!params.templateId || !params.businessUnitId || !params.domain)
    ) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);

    return onAdd(prompt, params)
      .then(() => {
        setError(undefined);
        setParams({});
        setPrompt(undefined);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message || e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedAccount, dynamicPage, open, type, params, onAdd, prompt]);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setParams({});
    setPrompt(undefined);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
      title="Would you like to add some context?"
    >
      <div className="mt-0 flex flex-col gap-4">
        {type === 'comparisonList' || type === 'comparisonTopItems' ? (
          <div className="flex flex-col justify-start gap-1">
            <FieldTitle title="Category" />
            <Select
              value={params.categoryId}
              onValueChange={(categoryId: string) => {
                const category = comparisonCategories?.find(
                  (c) => c.id === categoryId,
                );
                setParams((p) => ({
                  ...p,
                  categoryId: category?.id,
                  categoryName: category?.name,
                }));
              }}
              disabled={!comparisonCategories}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {comparisonCategories?.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <span className="flex items-center gap-2">
                      <TagIcon className="h-4 w-4" />
                      {category.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <></>
        )}
        {type === 'form' || type === 'contactUs' ? (
          <div className="flex flex-col justify-start gap-1">
            <FieldTitle title="Form" />
            <Select
              value={params.formId || (dynamicPage as LeadsDynamicPage)?.formId}
              onValueChange={(formId: string) => {
                setParams((p) => ({
                  ...p,
                  formId: formId,
                }));
              }}
              disabled={!forms}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {forms?.map((form) => (
                  <SelectItem key={form.id} value={form.id}>
                    {form.data().name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <></>
        )}
        {type === 'trustpilot' ? (
          <>
            <Input
              title="Template Id"
              value={params.templateId}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  templateId: e.target.value,
                }));
              }}
            />
            <Input
              title="Business Unit Id"
              value={params.businessUnitId}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  businessUnitId: e.target.value,
                }));
              }}
            />
            <Input
              title="Domain"
              value={params.domain}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  domain: e.target.value,
                }));
              }}
            />
          </>
        ) : (
          <></>
        )}
        <TextArea
          title="Prompt (optional)"
          placeholder="Write your instructions here..."
          value={prompt ?? ''}
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export function AddSectionApprovalModal({
  dynamicPageId,
  websiteId,
  variantId,
  section,
  open,
  onClose,
  onAdd,
}: Props) {
  const { selectedAccount } = useContext(UserContext);

  const createSection = useCallback(
    (
      type: CreatableWebsiteSectionType,
      prompt?: string,
      params?: Record<string, any>,
    ) => {
      if (!selectedAccount || !dynamicPageId) return;
      if (!variantId) {
        throw new Error('No variant found');
      }

      return createWebsiteSection(
        selectedAccount.id,
        dynamicPageId,
        variantId,
        websiteId,
        { ...section, type },
        prompt,
        params,
      ).then((res) => {
        onAdd(res.section);
      });
    },
    [selectedAccount, dynamicPageId, variantId, websiteId, section, onAdd],
  );

  return (
    <InnerAddSectionApprovalModal
      type={(section?.type || 'hero') as CreatableWebsiteSectionType}
      open={open}
      onClose={onClose}
      onAdd={async (prompt, params) => {
        return createSection(
          (section?.type || 'hero') as CreatableWebsiteSectionType,
          prompt,
          params,
        );
      }}
    />
  );
}
